import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, CreditCard } from "lucide-react";
import { useCheckInSend, useCheckInContext } from "../context";
import { useAudio } from "../../../../../lib/use-audio";
import { canRenderKimAddressQrCode, KimAddressQrCode } from "./KimAddressQrCode";
import { FEATURE_FLAGS } from "../../../../../platform/defaults";
import { useKimAddress } from "../../../../../lib/use-kim-address";
import { Tile } from "../../../components/Tile";
import { KioskStep } from "../../../../../components/kiosk-step";

/**
 * eEB prompt step: ways to prove statutory insurance without a card.
 *
 * With eEB enabled the patient can request the proof electronically
 * (REQUEST_EEB) or pick the insurer (SELECT_INSURER). The insurance-app QR is
 * always offered, with one instruction (KC-47). After a failed request it is
 * the main path, titled as an instruction rather than an alarm (KC-40). "Weiter"
 * continues without waiting for the proof.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <EebPromptStep />
 * ```
 */
export function EebPromptStep() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const context = useCheckInContext();
  const { kimInfo } = useKimAddress();

  useAudio("checkIn-eebPrompt");

  const showFailedMessage = context.eebStatus === "fail";
  const showElectronicOptions = FEATURE_FLAGS.eeb && !showFailedMessage;

  return (
    <KioskStep
      width="default"
      title={
        showFailedMessage
          ? t("self-service.check-in.eeb-prompt.title-app")
          : t("self-service.check-in.eeb-prompt.title")
      }
      lead={
        showFailedMessage
          ? t("self-service.check-in.eeb-prompt.subtitle-app")
          : t("self-service.check-in.eeb-prompt.subtitle")
      }
      primaryAction={{
        label: t("common.next"),
        icon: ArrowRight,
        onClick: () => send({ type: "SKIP_EEB" }),
        testId: "eeb-skip-button",
      }}
    >
      {showElectronicOptions ? (
        <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
          <Tile
            tone="blue-ink"
            className="min-h-44"
            data-testid="eeb-request-card"
            label={t("self-service.check-in.eeb-prompt.recommended")}
            name={t("self-service.check-in.eeb-prompt.verify-card")}
            desc={t("self-service.check-in.eeb-prompt.verify-card-description")}
            icon={<CreditCard />}
            onClick={() => send({ type: "REQUEST_EEB" })}
          />
          <Tile
            tone="sky-subtle"
            className="min-h-44"
            data-testid="eeb-select-insurer-card"
            name={t("self-service.check-in.eeb-prompt.select-insurer")}
            desc={t("self-service.check-in.eeb-prompt.select-insurer-description")}
            icon={<Building2 />}
            onClick={() => send({ type: "SELECT_INSURER" })}
          />
        </div>
      ) : null}

      {kimInfo && canRenderKimAddressQrCode(kimInfo.kimAddress) ? (
        <div data-testid="eeb-qr-code" className="mx-auto w-full max-w-md min-w-0">
          <KimAddressQrCode
            kimAddress={kimInfo.kimAddress}
            practiceName={kimInfo.practiceName}
            size={200}
          />
        </div>
      ) : null}
    </KioskStep>
  );
}
